/**
 *
 * AcceptInvite
 *
 * Landing page for an employee invite link (/accept-invite/:token). The invitee
 * sets their own password here; only then is the account created server-side.
 */
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
} from '@heltarchat/ui';

import api from 'app/api';
import {
  errorModal,
  receiveError,
  successModal,
} from 'utils/showSuccessErrorModal';
import { ErrorInterface } from 'utils/types/success-error-types';
import { InputPasswordByState } from '../AtomComponents/InputPassword';
import {
  PasswordRequirements,
  isPasswordValid,
} from '../AtomComponents/PasswordRequirements';
import { Loader } from '../AtomComponents/Loader';

export const AcceptInvite = memo(() => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid'>(
    'loading',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/auth/invite/${token}`);
        if (!active) return;
        setEmail(res?.data?.data?.email ?? '');
        setStatus('valid');
      } catch {
        if (active) setStatus('invalid');
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const onSubmit = async e => {
    e.preventDefault();
    if (!isPasswordValid(password)) {
      errorModal(
        t(
          'Please choose a stronger password that meets all the requirements below.',
        ),
      );
      return;
    }
    if (password !== confirmPassword) {
      errorModal(t('Passwords do not match!'));
      return;
    }
    try {
      setSubmitting(true);
      await api.post('/auth/accept-invite', { token, password });
      successModal(t('Your account is ready. Please log in.'));
      navigate('/');
    } catch (error) {
      receiveError((error as ErrorInterface)?.response?.data);
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className='flex h-[var(--app-height,100vh)] items-center justify-center'>
        <Loader />
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className='flex h-[var(--app-height,100vh)] items-center justify-center px-2'>
        <Card className='w-full max-w-md shadow-xl'>
          <CardHeader>
            <CardTitle className='text-xl'>{t('Invitation invalid')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <p className='text-sm text-[var(--heltar-text-secondary)]'>
              {t(
                'This invitation link is invalid or has expired. Please ask your administrator to send a new one.',
              )}
            </p>
            <Button
              className='w-full h-11 !btn-heltar'
              onClick={() => navigate('/')}
            >
              {t('Go to login')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='flex h-[var(--app-height,100vh)] items-center justify-center px-2'>
      <Card className='w-full max-w-md shadow-xl max-h-[85vh] overflow-y-auto'>
        <CardHeader className='space-y-1 text-center'>
          <CardTitle className='text-2xl font-bold'>
            {t('Set your password')}
          </CardTitle>
          {email && (
            <p className='text-sm text-[var(--heltar-text-secondary)]'>
              {email}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className='space-y-4'>
            <div>
              <Label htmlFor='password' className='mb-1 block'>
                {t('Password')}
              </Label>
              <InputPasswordByState
                className='h-11'
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              <PasswordRequirements value={password} className='mt-3' />
            </div>
            <div className='space-y-1'>
              <Label htmlFor='confirmPassword'>{t('Confirm Password')}</Label>
              <InputPasswordByState
                className='h-11'
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <p className='mt-1 text-xs text-[var(--heltar-state-error)]'>
                  {t('Passwords do not match.')}
                </p>
              )}
            </div>
            <Button
              type='submit'
              disabled={
                submitting ||
                !isPasswordValid(password) ||
                password !== confirmPassword
              }
              className='w-full h-11 !btn-heltar'
            >
              {submitting ? t('Activating…') : t('Activate account')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
});
